// Shared, read-only display for a RAG-grounded leave-entitlements summary
// (Slice 17, DESIGN §6.2 Action 1). Extracted from `routes/Portal.tsx` so the
// Manager-facing case policy view (PO Spec §1.1 "Show me the policy") can
// render the identical grounded/placeholder branches instead of duplicating
// the leaveDays/payStatus/citations layout.
//
// Presentational only — callers own data fetching and any instrumentation
// (e.g. the Employee portal's "Resources Accessed" recorder via `onGrounded`).
//
// Anti-anthropomorphism (DESIGN §1.4 #8): copy stays neutral and factual, no
// first-person plural, no availability promises.

import { useEffect, useRef } from 'react';
import type { EmployeeEntitlements } from '@besideu/shared/cases';

interface EntitlementsSummaryProps {
  entitlements: EmployeeEntitlements | null;
  title?: string;
  placeholder?: string;
  id?: string;
  /** Fired once, the first time a summary with real policy grounding (≥1 citation) renders. */
  onGrounded?: () => void;
}

export function EntitlementsSummary({
  entitlements,
  title = 'Entitlements',
  placeholder = "Specific leave and pay details will appear here once your organisation's policy documents are connected.",
  id,
  onGrounded,
}: EntitlementsSummaryProps) {
  const hasPolicyGrounding = !!entitlements && entitlements.citations.length > 0;
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (hasPolicyGrounding && !notifiedRef.current) {
      notifiedRef.current = true;
      onGrounded?.();
    }
  }, [hasPolicyGrounding, onGrounded]);

  if (!entitlements) {
    return (
      <div
        id={id}
        className="scroll-mt-24 rounded-lg border border-border bg-muted/40 p-6 text-sm text-muted-foreground"
      >
        <p className="font-medium text-foreground">{title}</p>
        <p className="mt-1">{placeholder}</p>
      </div>
    );
  }

  return (
    <div id={id} className="scroll-mt-24 space-y-4 rounded-lg border border-border p-6 text-sm">
      <p className="text-base font-medium">{title}</p>

      {(entitlements.leaveDays || entitlements.payStatus) && (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {entitlements.leaveDays && (
            <div>
              <dt className="text-xs tracking-wide text-muted-foreground uppercase">Leave</dt>
              <dd className="mt-1">{entitlements.leaveDays}</dd>
            </div>
          )}
          {entitlements.payStatus && (
            <div>
              <dt className="text-xs tracking-wide text-muted-foreground uppercase">Pay</dt>
              <dd className="mt-1">{entitlements.payStatus}</dd>
            </div>
          )}
        </dl>
      )}

      <p className="whitespace-pre-wrap text-muted-foreground">{entitlements.summary}</p>

      {entitlements.citations.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Based on:{' '}
          <span className="font-medium text-foreground">{entitlements.citations.join(', ')}</span>.
          For specifics, confirm with HR.
        </p>
      )}
    </div>
  );
}
